import React, {
  useState,
  useCallback,
  useEffect
} from "react";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";
import {
  ToastId,
  toast
} from "react-toastify";
import _ from "lodash";
import * as moment from "moment-timezone";
import HTMLParser from "html-react-parser";
import {
  useRouteMatch,
  useHistory,
  useLocation
} from "react-router-dom";
import { Formik } from "formik";

import { Text } from "src/library/components/Text/Text";
import Grid from "src/library/components/Hedron";
import { Datetime } from "src/library/components/Datetime";
import { Icon } from "src/library/components/Icon";
import {
  Select,
  IFormOption,
  Radio,
  Input,
  IFormField,
  Form
} from "src/library/components/Forms";
import { Icons } from "src/library/components/Icon/types";
import { useCurrentUser } from "src/core/users/hooks";
import { useOrder } from "src/core/orders/hooks";
import { PageProps } from "src/pages/Router/routes";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import RespondTextAreaContainer from "src/modules/support/components/RespondTextAreaContainer";
import CreateSupportTicketButtons from "src/modules/support/components/CreateSupportTicket/CreateSupportTicketButtons";
import {
  useSupportApi,
  CreateSupportTicketParams,
  Origin,
  SupportTicketTypeOptions
} from "src/modules/support/api/support.api";
import type { IStore } from "src/core/shared/store";
import CreditRequestContextProvider, {
  CreditRequestContextProviderRef,
  CreditRequestRowType
} from "src/modules/credit-request/context/CreditRequestContext";
import useDrop from "src/core/drops/hooks/useDrop";
import OrderStatus from "src/core/orders/components/OrderStatus";
import {
  IOrder,
  IOrderStatus
} from "src/core/orders/api/orders.api";
import { IDrop } from "src/core/drops/api/drops.api";
import { DateInput } from "src/core/shared/components/Date/DateInput";
import DropOrdersTable from "src/pages/Admin/CustomersAndUsers/Customer/Drops/DropOrdersTable";
import SelectedDrop from "src/core/drops/components/SelectedDrop";
import { FilterParams } from "src/library/utils";
import { Button } from "src/library/components/Button";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";
import { parseSupportTicketExtraDataFormAnswers } from "src/modules/support/utils/index";
import { DropZoneRef } from "src/core/shared/components/dropzones/DropZoneMultiFile";
import { useAdditionalDataFormFields } from "src/modules/support/hooks/useAdditionalDataFormFields";
import { FileData } from "src/core/shared/components/files/ListFiles";

/** Defines props for the handle raise ticket callback used to handle ticket submission */
interface HandleRaiseTicketProps {
  /** Data that came from the credit request component */
  creditsData?: Partial<CreateSupportTicketParams>;
}

/**
 * This create support ticket page can either be used from a clean start,
 * ie no order context, or used in conjuction with currentOrder.
 * To use as clean start, when routing here using history.push, include { cleanStart: true } in options.
 */
const CreateSupportTicketPage: React.FC<PageProps<{orderId: string, dropId: string}>> = ({ title, ...props }) => {
  const { match, history } = props;
  const [ postTicketRes, postTicketReq ] = useSupportApi("CREATE_SUPPORT_TICKET");
  const [ details, setDetails ] = useState("");
  const [ issueTypeId, setIssueTypeId ] = useState("");
  const [ { currentUser, currentRole } ] = useCurrentUser();
  const dropId = match && match.params?.dropId ? parseInt(match.params.dropId, 0) : 0;
  const location = useLocation<{cleanStart: boolean; context: "drop" | "support"}>();
  const orderId = React.useMemo(() => match?.params.orderId ? parseInt(match.params.orderId as string, 0) : null, [ match?.params.orderId ]);
  const CreditsContextRef = React.useRef<CreditRequestContextProviderRef>(null);
  const [ { drop } ] = useDrop(dropId);
  const [ { currentOrder } ] = useOrder();

  // Used to determine user journy. Ie, if false, the ability to change selected drop will not be accessible
  const cleanStart = React.useMemo(() => {
    return location?.state?.cleanStart;
  }, [ location?.state?.cleanStart ]);

  const context = React.useMemo(() => {
    return location?.state?.context;
  }, [ location?.state?.context ]);

  const [ selectedDrop, setSelectedDrop ] = React.useState<IDrop | null>(!cleanStart || context === "drop" ? drop : null);
  const [ selectedOrder, setSelectedOrder ] = React.useState<IOrder | null>(cleanStart ? null : currentOrder.data);
  const [ selectedOrigin, setSelectedOrigin ] = React.useState<Origin| null>(Origin.Email);
  const [ timeTaken, setTimeTaken ] = useState<number | null>(null);
  const [ dateOfContact, setDateOfContact ] = React.useState<Date>(moment.utc().toDate());
  const [ showOrderOptions, setShowOrderOptions ] = React.useState(false);
  const [ showSupportTicketForm, setShowSupportTicketForm ] = React.useState(false);
  //Controls display of the WYSIWYG editor
  const [ showResponseSection, setShowResponseSection ] = React.useState(true);
  const createTicketView: "admin" | "customer" = currentRole?.type || "customer";
  // Controls whether or not to show the WYSIWYG editor
  const [ showWysiwyg, setShowWysiwyg ] = React.useState(true);
  // Track validation errors for the additional data form input.
  const [ additionalDataFormFields, setAdditionalDataFormFields ] = React.useState<IFormField[]>([]);
  const formRef = React.useRef<Formik>(null);
  const dropzoneRef = React.useRef<DropZoneRef>(null);
  const [ attachmentKeys, setAttachmentKeys ] = useState<string[]>([]);
  const { generateAdditionalFields } = useAdditionalDataFormFields();

  const {
    orderStatuses, supportTicketTypes, terminology, masqueradeMode
  } = useMappedState((store: IStore) => ({
    masqueradeMode: store.global.masqueradeMode,
    orderStatuses: store.global.settings ? store.global.settings.orderStatuses : [],
    supportTicketTypes: store.global.settings?.supportTicketTypes,
    terminology: store.global.settings?.terminology
  }));

  // selected ticket type
  const selectedTicketType = React.useMemo(() => {
    return _.find(supportTicketTypes, type => type.id.toString() === issueTypeId);
  }, [ issueTypeId, supportTicketTypes ]);

  const allowedTicketTypeFormOptions: IFormOption[] = React.useMemo(() => {
    if (supportTicketTypes) {
      let supportTicketTypesToDisplay = supportTicketTypes;

      if (createTicketView === "customer") {
        supportTicketTypesToDisplay = _.filter(supportTicketTypesToDisplay, st => !st.options.adminView);
      }

      // if its a clean start, offer all options
      if (cleanStart && createTicketView === "admin") {
        return supportTicketTypesToDisplay.map(type => ({
          text: type.name,
          value: type.id.toString()
        }));
      }

      // otherwise restrict based on options
      return _.map(_.filter(supportTicketTypesToDisplay, ticketType => {
        if (ticketType.options.order && !orderId) {
          return false;
        } else if (orderId && !ticketType.options.order) {
          return false;
        }

        return true;
      }), type => ({
        text: type.name,
        value: type.id.toString()
      }));
    }

    return [];
  }, [
    cleanStart,
    createTicketView,
    orderId,
    supportTicketTypes
  ]);

  // Options object for the currently selected support ticket type
  const selectedIssueTypeOptions: SupportTicketTypeOptions = React.useMemo(() => {
    return selectedTicketType?.id ? _.find(supportTicketTypes, type => type.id.toString() === selectedTicketType.id?.toString())?.options || {} : {};
  }, [ selectedTicketType?.id, supportTicketTypes ]);

  // Message used to inform user of an invalid selected order delivery date. Differs based on ticket type config
  const invalidOrderDeliveryDateMessage = React.useMemo(() => {
    if (typeof selectedIssueTypeOptions?.allowCredits === "object" && selectedIssueTypeOptions?.allowCredits?.validDeliveryWindow?.errorMessage) {
      return selectedIssueTypeOptions.allowCredits.validDeliveryWindow.errorMessage;
    } else {
      return "Order delivery date falls outside of the valid range";
    }
  }, [ selectedIssueTypeOptions.allowCredits ]);

  // For the current ticket type, keep track of ids and names of the valid order status codes
  const validOrderStatusCodes = React.useMemo(() => {
    const permittedStatusCodeIds: number[] = [];
    const permittedStatusCodeNames: string[] = [];

    if (typeof selectedIssueTypeOptions.order !== "boolean" && selectedIssueTypeOptions.order?.statusCodes) {
      const selectedIssueValidStatusCodes = selectedIssueTypeOptions.order.statusCodes;

      // Isolate order status ids of the order status names listed by the order config for this ticket type
      _.map(selectedIssueValidStatusCodes, validStatusCode => {
        const orderStatus = _.find(orderStatuses, (os: IOrderStatus) => os.code === validStatusCode);

        if (orderStatus) {
          permittedStatusCodeIds.push(orderStatus.id);
          permittedStatusCodeNames.push(orderStatus.name);
        }
      });
    }

    return {
      ids: permittedStatusCodeIds,
      names: permittedStatusCodeNames
    };
  }, [ orderStatuses, selectedIssueTypeOptions.order ]);

  // Validate the currently selected order status code
  const orderStatusCodeIsValid = React.useMemo(() => {
    if (selectedOrder && validOrderStatusCodes.ids.length !== 0) {
      return !!_.find(validOrderStatusCodes.ids, validId => validId === selectedOrder?.orderStatus?.id);
    }

    return true;
  }, [ selectedOrder, validOrderStatusCodes.ids ]);

  // Validates order delivery date against support ticket type config if it has been configured, validation passes in absense of this config
  const isOrderDeliveryDateValid = React.useMemo(() => {
    if (currentRole?.type === "customer") {
      if (typeof selectedIssueTypeOptions.allowCredits === "object" && selectedIssueTypeOptions.allowCredits.validDeliveryWindow && selectedOrder) {
        const orderDeliveryDate = moment.utc(selectedOrder.orderDeliveryDate);

        if (selectedIssueTypeOptions.allowCredits.validDeliveryWindow.minDaysAfterDelivery) {
          const minimumCreditDate = moment.utc(orderDeliveryDate).add(selectedIssueTypeOptions.allowCredits.validDeliveryWindow.minDaysAfterDelivery, "d");

          if (moment.utc().isBefore(minimumCreditDate, "d")) {
            return false;
          }
        }

        if (selectedIssueTypeOptions.allowCredits.validDeliveryWindow.maxDaysAfterDelivery) {
          const maximumCreditDate = moment.utc(orderDeliveryDate).add(selectedIssueTypeOptions.allowCredits.validDeliveryWindow.maxDaysAfterDelivery, "d");

          if (maximumCreditDate.isBefore(moment.utc(), "d")) {
            return false;
          }
        }
      }
    }

    return true;
  }, [
    currentRole?.type,
    selectedIssueTypeOptions.allowCredits,
    selectedOrder
  ]);

  // Make form visibility and questions reflect the current selected issue config.
  React.useEffect(() => {
    const formFields = generateAdditionalFields(selectedIssueTypeOptions.formQuestions ?? []);

    setAdditionalDataFormFields(formFields);
    setShowSupportTicketForm(!!selectedIssueTypeOptions.formQuestions);
  }, [ selectedIssueTypeOptions ]);

  // Hide Wysiwyg if disabled in selected issue type config
  React.useEffect(() => {
    if (selectedIssueTypeOptions?.showWysiwyg === false) {
      setShowWysiwyg(false);
    } else {
      setShowWysiwyg(true);
    }
  }, [ selectedIssueTypeOptions ]);

  // generates the required filters used when requesting orders
  const requiredFiltersForOrdersTable: FilterParams<IOrder> = React.useMemo(() => {
    const orderTableFilters: FilterParams<IOrder> = {};

    if (typeof selectedIssueTypeOptions.order !== "boolean" && selectedIssueTypeOptions.order?.statusCodes) {
      orderTableFilters.orderStatusId = validOrderStatusCodes.ids;
    }

    return orderTableFilters;
  }, [ selectedIssueTypeOptions.order, validOrderStatusCodes.ids ]);

  // Accepts an array of credit request rows, and returns a string explaining how many products were requested for credit out of the total order products
  const formatTotalProductsForCredit = React.useCallback((credits: CreditRequestRowType[]) => {
    if (selectedOrder) {
      const totalOrderProductQuantity = _.reduce(selectedOrder.orderLines, (total, orderLine) => total + orderLine.quantity, 0);
      const totalCreditProductQuantity = _.reduce(credits, (total, creditLine) => total + creditLine.quantityRequested, 0);

      return `Credit requested for ${totalCreditProductQuantity} out of ${totalOrderProductQuantity} total products.`;
    }

    return null;
  }, [ selectedOrder ]);

  // Handle ticket submission
  const handleRaiseTicket = useCallback(({ creditsData }: HandleRaiseTicketProps): void | ToastId => {
    const ticketDrop = createTicketView === "customer" ? drop : selectedDrop || selectedOrder?.branch;

    if (selectedIssueTypeOptions.order && !selectedOrder) {
      return toast.error("This ticket issue type requires an order to be associated. Please select an order.");
    }

    //Validate the order delivery date. Errors with custom validation failure message if one is configured.
    if (!isOrderDeliveryDateValid) {
      return toast.error(invalidOrderDeliveryDateMessage);
    }

    // validate status code before sending request if statusCode required
    if (!orderStatusCodeIsValid) {
      if (validOrderStatusCodes.names.length === 1) {
        return toast.error(`This ticket issue type requires an order with a status of ${validOrderStatusCodes.names[ 0 ]} to proceed.`);
      } else {
        return toast.error(`This ticket issue type requires an order with one of the valid statuses: [${validOrderStatusCodes.names.join(", ")}] to proceed.`);
      }
    }

    if (!ticketDrop) {
      return toast.error(`All support tickets require to be associated with a ${_.startCase(terminology?.drop.singular.text || "drop")}`);
    }

    // Halt submission if any form field validation errors exist
    if (additionalDataFormFields.find(field => field.error)) {
      return toast.error("Support ticket form responses did not pass validation.");
    }

    if (currentUser) {
      let ticketBodyDetails = "";

      if (creditsData?.credits) {
        ticketBodyDetails += formatTotalProductsForCredit(creditsData.credits);
      }

      const additionalFormData = formRef.current?.getFormikContext().values;

      // If extra form data is enabled, prepend answers to the body details.
      if (showSupportTicketForm && additionalFormData) {
        ticketBodyDetails += parseSupportTicketExtraDataFormAnswers(additionalDataFormFields, additionalFormData);
      }

      // details set with <p></p> tags and so we're looking for a length of more than 8 to determine if there is any content.
      if (details.length > 8) {
        ticketBodyDetails += details;
      }

      // collate evidence from attachments and addtional data form
      const evidence: string[] = [ ...attachmentKeys || [] ];

      if (additionalFormData) {
        additionalDataFormFields.forEach((field: IFormField) => {
          if (field.type === "attachments") {
            // check if there is a value assoicated to this field
            if (additionalFormData[ field.name ]) {
              // grab the s3FileKey and map to the evidence array
              additionalFormData[ field.name ].map((file: FileData) => evidence.push(file.s3FileKey));
            }
          }
        });
      }

      const createTicketData: CreateSupportTicketParams = {
        ...creditsData,
        supportTicketTypeId: parseInt(issueTypeId),
        branchId: ticketDrop.id,
        body: ticketBodyDetails,
        userId: currentUser.id,
        orderId: selectedOrder?.id,
        origin: createTicketView === "admin" ? selectedOrigin : Origin.Customer,
        dateOfContact,
        evidence
      };

      // validate time taken and add to createTicketData if exists
      if (timeTaken) {
        createTicketData.timeTaken = timeTaken;
      }

      return postTicketReq(createTicketData);
    } else {
      // should not get this far as validating data before sending.
      return toast.error("Support ticket form not complete. Please check and try again.");
    }
  }, [
    additionalDataFormFields,
    createTicketView,
    currentUser,
    dateOfContact,
    details,
    drop,
    formatTotalProductsForCredit,
    invalidOrderDeliveryDateMessage,
    isOrderDeliveryDateValid,
    issueTypeId,
    orderStatusCodeIsValid,
    postTicketReq,
    selectedDrop,
    selectedIssueTypeOptions.order,
    selectedOrder,
    selectedOrigin,
    showSupportTicketForm,
    terminology?.drop.singular.text,
    timeTaken,
    validOrderStatusCodes.names,
    attachmentKeys
  ]);

  useEffect(() => {
    CreditsContextRef.current?.setSupportTicket(null);

    // If succesfully created ticket go to ticket
    if (postTicketRes.data) {
      CreditsContextRef.current?.setSupportTicket(postTicketRes.data);

      if (location?.state?.context === "drop") {
        history.push(postTicketRes.data.id.toString());
      } else {
        history.push(`/support/tickets/${postTicketRes.data.id}`);
      }
    }

    // if failed to create ticket show an error
    if (postTicketRes.error) {
      CreditsContextRef.current?.setSupportTicket(null);
      toast.error(postTicketRes.error.message);
    }
  }, [
    history,
    location?.state?.cleanStart,
    postTicketRes.data,
    postTicketRes.error
  ]);

  const canUseTicketType = React.useMemo(() => {
    if (selectedIssueTypeOptions.order) {
      if (!selectedOrder) {
        return false;
      }

      if (typeof selectedIssueTypeOptions.order === "object") {
        if (selectedIssueTypeOptions.order.statusCodes && !_.find(selectedIssueTypeOptions.order.statusCodes, code => code === selectedOrder.orderStatus?.code)) {
          return `The order status must be one of [${validOrderStatusCodes.names.join(", ")}] for this type of ticket`;
        }
      }
    }

    if (selectedIssueTypeOptions.allowCredits && !selectedOrder?.id) {
      return false;
    }

    return true;
  }, [
    selectedIssueTypeOptions.order,
    selectedIssueTypeOptions.allowCredits,
    selectedOrder,
    validOrderStatusCodes.names
  ]);

  // Is in context of credit request
  const isCreditRequest = React.useMemo(() => (
    !!(canUseTicketType === true && !!selectedIssueTypeOptions.allowCredits && issueTypeId && isOrderDeliveryDateValid)
  ), [
    canUseTicketType,
    selectedIssueTypeOptions.allowCredits,
    issueTypeId,
    isOrderDeliveryDateValid
  ]);

  const clearAttachmentsInDropzone = React.useCallback(() => {
    if (dropzoneRef.current) {
      dropzoneRef.current.clearFilesData();
    }
  }, [ dropzoneRef ]);

  const clearAttachments = React.useCallback(() => {
    setAttachmentKeys([]);
    clearAttachmentsInDropzone();
  }, [ clearAttachmentsInDropzone ]);

  // Controls display of FAQ answer
  const showFAQAnswer = React.useMemo(() => {
    return !!(selectedIssueTypeOptions && selectedIssueTypeOptions.faq?.enabled && selectedIssueTypeOptions.faq.body);
  }, [ selectedIssueTypeOptions ]);

  // When the showFAQAnswer updates, we want the inverse applied to showResponseSection
  React.useEffect(() => {
    setShowResponseSection(!showFAQAnswer);
  }, [ showFAQAnswer ]);

  // show order options if issueType requires an order and no order selected
  React.useEffect(() => {
    if (createTicketView === "admin" && selectedIssueTypeOptions.order && selectedDrop && !selectedOrder) {
      setShowOrderOptions(true);
    }
  }, [
    createTicketView,
    selectedDrop,
    selectedIssueTypeOptions?.order,
    selectedOrder
  ]);

  // reset the order every time the drop changes if in context of cleanStart
  React.useEffect(() => {
    if (cleanStart) {
      setSelectedOrder(null);
      // make sure attachments state is cleared
      clearAttachments();
    }
  }, [
    cleanStart,
    clearAttachments,
    selectedDrop
  ]);

  // when issue type changes, hide order options if not required
  React.useEffect(() => {
    // simply using !selectedIssueTypeOptions.order did not return desired result
    if (!Object.keys(selectedIssueTypeOptions).includes("order")) {
      setShowOrderOptions(false);
    }

    // clear the attachments if populated
    clearAttachments();
  }, [ clearAttachments, selectedIssueTypeOptions ]);

  return (
    <Layout
      title={title}
      {...props}
    >
      <MainContentWrapper>
        <Wrapper
          direction="vertical"
          width="100%"
        >
          {masqueradeMode?.active && (
            <ErrorNotice
              type="warn"
              message="Cannot create support tickets in masquerade mode"
            />
          )}

          {selectedOrder?.id && (
            <SelectedOrderInfo
              direction="horizontal"
              data-tip
              data-for="amend-selected-order"
            >
              <OrderText onClick={() => history.push(`/orders/${orderId}`)}>
                <Icon
                  icon={Icons.CHEVRONLEFT}
                  size={20}
                  $fillColor="darkGrey"
                />

                <Text
                  className="order-text"
                  fontSize="h2"
                  weight="light"
                  style={{ marginBottom: 0 }}
                >
                  Order
                </Text>

                <Text
                  className="order-text"
                  fontSize="h2"
                  weight="bold"
                  style={{ marginBottom: 0 }}
                >
                  {`#${selectedOrder.id}`}
                </Text>
              </OrderText>

              <DeliveryDate>
                <Text weight="bold">
                  {"Delivery "}
                </Text>

                <Text className="order-text">
                  <Datetime
                    ignoreLocal
                    date={selectedOrder?.orderDeliveryDate || null}
                    template="long_date"
                  />
                </Text>

                <Text
                  className="order-text"
                  weight="bold"
                >
                  {selectedOrder?.orderStatus && (
                    <OrderStatus
                      tooltipPlace="left"
                      orderId={selectedOrder.id}
                      status={selectedOrder?.orderStatus}
                    />
                  )}
                </Text>
              </DeliveryDate>
            </SelectedOrderInfo>
          )}

          {createTicketView === "customer" && (
            <Text as="p">
              Raise a support ticket and our customer support team will get back to you as soon
              as possible. Please be as descriptive as possible so we can help resolve your problem.
            </Text>
          )}

          {createTicketView === "admin" && (
            <div className="section">
              <div className="origin-contact">
                <div className="origin">
                  <Text as="h4">
                    Origin:
                  </Text>

                  <Radio
                    options={[
                      {
                        text: "Phone",
                        value: "1",
                        checked: selectedOrigin === Origin.Phone
                      },
                      {
                        text: "Email",
                        value: "0",
                        checked: selectedOrigin === Origin.Email
                      }
                    ]}
                    handleChange={(event: React.ChangeEvent<HTMLInputElement>) => setSelectedOrigin(event.target.value === "1" ? Origin.Phone : Origin.Email)}
                    name="template"
                    autoHideLabels
                    label=""
                    value={selectedOrigin === Origin.Phone ? "1" : "0"}
                  />
                </div>

                <div className="date-of-contact">
                  <Text as="h4">
                    Date of Contact:
                  </Text>

                  <DateInput
                    label="Start Date"
                    date={moment.utc(dateOfContact).toDate()}
                    setDate={date => setDateOfContact(date)}
                    minimumDate={moment.utc().subtract(10, "years").toDate()}
                    maximumDate={moment.utc().toDate()}
                  />
                </div>
              </div>
            </div>
          )}

          {createTicketView === "admin" && (
            <div className="section">
              <Text as="h4">
                {`${_.startCase(terminology?.drop.singular.text || "drop")}:`}
              </Text>

              <SelectedDrop
                drop={selectedDrop}
                handleUpdate={drop => setSelectedDrop(drop)}
                terminology={terminology}
                disableRemoveDrop={context === "drop"}
                disabled={context === "drop"}
              />
            </div>
          )}

          <div className="section">
            <Text as="h4">
              Issue Type:
            </Text>

            <Select
              as="select"
              label="Issue type"
              autoHideLabels
              name="supportIssueType"
              value={issueTypeId}
              handleChange={e => setIssueTypeId(e.target.value)}
              options={allowedTicketTypeFormOptions}
              error={typeof canUseTicketType === "string" ? canUseTicketType : undefined}
            />
          </div>

          {selectedDrop && terminology && cleanStart && (
            <div className="section">
              {selectedOrder && !showOrderOptions ? (
                <div className="selected-order">
                  <div className="info">
                    <Text as="h4">
                      Selected Order:
                    </Text>

                    <Text>
                      <Datetime
                        ignoreLocal
                        date={selectedOrder.orderDeliveryDate}
                        template="long_date"
                      />
                    </Text>
                  </div>

                  <Button
                    $buttonType="plain"
                    onClick={() => {
                      if (cleanStart) {
                        setSelectedOrder(null);
                        setShowOrderOptions(true);
                      }
                    }}
                  >
                    Change Selected Order
                  </Button>
                </div>
              ) :
                showOrderOptions && (
                  <>
                    <Text as="h4">
                      Select Order
                    </Text>

                    <DropOrdersTable
                      dropId={selectedDrop.id}
                      terminology={terminology}
                      customCallback={order => {
                        setShowOrderOptions(false);
                        setSelectedOrder(order);
                      }}
                      requiredFilters={requiredFiltersForOrdersTable}
                      nonFilterableColumns={[ "customerReference" ]}
                      selectedColumns={[
                        "id",
                        "orderDeliveryDate",
                        "customerReference",
                        "orderStatus",
                        "cartTotal",
                        "actions"
                      ]}
                    />
                  </>
                )}
            </div>
          )}

          {/** Error message for invalid order delivery date displayed in scenario of a single order selection (!cleanStart) */}
          {!cleanStart && !isOrderDeliveryDateValid && (
            <div>
              <Text style={{ color: "red" }}>
                {invalidOrderDeliveryDateMessage}
              </Text>
            </div>
          )}

          {/** FAQ answer display */}
          {showFAQAnswer && (
            <div className='section'>
              <FAQAnswer>
                <Icon
                  className="info-icon"
                  icon={Icons.INFO}
                  size={25}
                  $fillColor="primaryDark"
                />

                <div>
                  {selectedIssueTypeOptions.faq?.title && (
                    <Text as="h3">
                      {selectedIssueTypeOptions.faq.title}
                    </Text>
                  )}

                  {selectedIssueTypeOptions.faq?.body && HTMLParser(selectedIssueTypeOptions.faq?.body)}

                  {/** If config specifies so, allow a support ticket to be enabled in addition to showing the FAQ answer */}
                  {selectedIssueTypeOptions.faq?.raiseTicketEnabled && (
                    <div className="faq-continue-button">
                      <Button
                        $buttonType="plain"
                        onClick={() => {
                          setShowResponseSection(true);
                        }}
                      >

                        <Text style={{ textDecoration: "underline" }}>
                          Continue to raise support ticket
                        </Text>
                      </Button>
                    </div>
                  )}
                </div>
              </FAQAnswer>
            </div>
          )}

          {showResponseSection && (
            <>

              {/** Support ticket additional data form */}
              {showSupportTicketForm && selectedIssueTypeOptions?.formQuestions && (
                <>
                  <Form
                    error={null}
                    submitting={false}
                    fields={additionalDataFormFields}
                    onSubmit={() => {
                      // Call the ticket submission logic with the custom form data that was gathered
                      handleRaiseTicket({});
                    }}
                    ref={formRef}
                  />
                </>
              )}

              {/** WYSIWYG editor */}
              <div className="section">
                <RespondTextAreaContainer
                  label={showSupportTicketForm ? "Additional information:" : ""}
                  handleChange={value => setDetails(value)}
                  dropzoneRef={dropzoneRef}
                  updateAttachments={setAttachmentKeys}
                />
              </div>
            </>
          )}

          {/** Credit request */}
          {isCreditRequest && (
            <div className="section">
              <CreditRequestContextProvider
                saveSupportTicket={creditsData => handleRaiseTicket({ creditsData })}
                supportTicket={{
                  id: 0,
                  orderId: selectedOrder?.id,
                  supportTicketTypeId: parseInt(issueTypeId, 10)
                }}
              />
            </div>
          )}

          <div className="section">
            {createTicketView === "admin" && showResponseSection && (
              <>
                <div className="time-taken-input">
                  <Text as="h4">
                    Time Taken:
                  </Text>

                  <Input
                    as="input"
                    inputType="number"
                    name="time-taken"
                    autoHideLabels
                    label="Time Taken"
                    placeholder=""
                    value={timeTaken}
                    handleChange={e => {
                      setTimeTaken(e.target.value);
                    }}
                  />
                </div>

                <div className="time-taken-instruction">
                  <Text as="sub">
                    minutes
                  </Text>
                </div>
              </>
            )}

            {/** Create support ticket buttons */}
            <Grid.Bounds className="buttons-container">
              <CreateSupportTicketButtons
                history={history}
                onRaiseTicket={async () => {
                  // If there is a custom data input form in use, the ticket will be raised as part of the form submission
                  if (formRef.current) {
                    formRef.current.submitForm();
                  } else {
                    handleRaiseTicket({});
                  }
                }}
                isRaisingLoading={postTicketRes.pending}
                enableRaise={((!showWysiwyg || !!details.length) || showSupportTicketForm) && !!issueTypeId.length && !masqueradeMode?.active}
                hideRaiseButton={isCreditRequest}
              />
            </Grid.Bounds>
          </div>

        </Wrapper>
      </MainContentWrapper>
    </Layout>
  );
};

const DeliveryDate = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-around;
`;

const OrderText = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
`;

/** Styled div for the FAQ answer section */
const FAQAnswer = styled.div`
  display: flex;
  flex-direction: row;
  background-color: white;
  border-radius: 5px;
  padding: 22px 22px 22px 18px;
  margin-top: 20px;
  div {
    display: flex;
    flex-direction: column;
    margin-left: 10px;
  }
  .button-wrapper {
    margin-left: -25px;
    margin-right: auto;
  }
  p {
    font-size: 18px;
  }
`;

const SelectedOrderInfo = styled(Grid.Bounds)`
  ${({ theme }) => css`
    background: ${theme.colors.white};
    padding: ${theme.spacing.padding.sm};
    margin-bottom: ${theme.spacing.margins.lg};
    border-radius: ${theme.borderRadius};
    align-items: center;
    justify-content: space-between;

    .order-text {
      margin-bottom: 0;
      margin-left: ${theme.spacing.margins.sm};
    }
  `};
`;

const Wrapper = styled(Grid.Bounds)`
  ${({ theme }) => css`
    .left-container {
      margin-right: ${theme.spacing.margins.sm};
    }

    .right-container {
      margin-left: ${theme.spacing.margins.sm};
    }

    .section {
      margin: ${theme.spacing.margins.md} 0;
    }

    .selected-order {
      .info {
        display: flex;
        
        span {
          margin-left: ${theme.spacing.margins.md};
        }
      }

      button {
        padding: 0;
      }
    }

    .time-taken-input {
      display: flex;
      justify-content: flex-end;
      align-items: center;
      margin-top: ${theme.spacing.margins.md};

      h4 {
        margin-bottom: 0;
        margin-right: ${theme.spacing.margins.sm};
      }
    }
    
    .time-taken-instruction {
      display: flex;
      justify-content: flex-end;
      margin-bottom: ${theme.spacing.margins.md};
    }

    .buttons-container {
      display: flex;
      justify-content: flex-end;
    }

    .origin-contact {
      display: flex;
      width: 400px;

      .origin,
      .date-of-contact {
        flex: 1;
      }
    }
  `};
`;

export default CreateSupportTicketPage;
